import { ExternalLink } from "lucide-react";
import { StarRating } from "./StarRating";
import type { Lender } from "@/lib/lenders";

const NAMES = [
  "Anna K.",
  "Johan L.",
  "Maria S.",
  "Erik N.",
  "Sofia H.",
  "Mikael P.",
  "Elin B.",
  "Fredrik A.",
  "Karin T.",
  "Daniel W.",
];

const TEMPLATES: ((n: string) => string)[] = [
  (n) => `Ansökte på kvällen och pengarna låg på kontot samma dag. Tydlig information om ränta och avgifter hos ${n}, inga överraskningar.`,
  (n) => `Enkelt att signera med BankID och snabbt svar på ansökan. Jag hade önskat något lägre ränta, men ${n} var tydliga med vad lånet faktiskt kostade.`,
  (n) => `Kundtjänsten svarade inom några minuter i chatten och hjälpte mig ändra förfallodagen. Bra bemötande hos ${n}.`,
  (n) => `Jag jämförde flera aktörer och valde ${n} eftersom villkoren var lättast att förstå. Amorteringsplanen fanns i appen direkt.`,
  (n) => `Fick lånet beviljat trots en äldre betalningsanmärkning. Räntan blev högre än snittet, men ${n} förklarade varför innan jag skrev på.`,
  (n) => `Löste in lånet i förtid utan extra avgift. Smidig process från början till slut med ${n}.`,
  (n) => `Utbetalningen gick via realtidsbetalning och tog några minuter. Rekommenderar ${n} om man har bråttom.`,
];

const SOURCES = ["Trustpilot", "Google", "Trustpilot", "Google", "Trustpilot"] as const;

function hash(s: string) {
  let h = 7;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) % 100000;
  return h;
}

function daysAgoLabel(d: number) {
  if (d === 0) return "Idag";
  if (d === 1) return "1 dag sedan";
  if (d < 7) return `${d} dagar sedan`;
  const w = Math.round(d / 7);
  return w === 1 ? "1 vecka sedan" : `${w} veckor sedan`;
}

function reviewUrl(lender: Lender, source: string) {
  const q = encodeURIComponent(lender.name);
  return source === "Google"
    ? `https://www.google.com/search?q=${q}+omd%C3%B6men`
    : `https://se.trustpilot.com/search?query=${q}`;
}

export function LenderReviews({ lender }: { lender: Lender }) {
  const seed = hash(lender.id);
  const items = Array.from({ length: 5 }).map((_, i) => {
    const nameIdx = (seed + i * 3) % NAMES.length;
    const tplIdx = (seed + i * 5) % TEMPLATES.length;
    const source = SOURCES[i]!;
    const stars = Math.max(
      3.5,
      Math.min(5, Number((lender.rating + (i % 3 === 0 ? 0.2 : i % 3 === 1 ? -0.3 : 0)).toFixed(1)))
    );
    return {
      name: NAMES[nameIdx]!,
      text: TEMPLATES[tplIdx]!(lender.name),
      source,
      stars,
      when: daysAgoLabel(2 + ((seed + i * 7) % 26)),
      href: reviewUrl(lender, source),
    };
  });

  return (
    <section aria-label={`Recensioner av ${lender.name}`} className="not-prose my-8">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <h2 className="font-display text-xl font-extrabold tracking-tight text-foreground sm:text-2xl">
          Senaste recensionerna om {lender.name}
        </h2>
        <div className="flex items-center gap-2">
          <StarRating rating={lender.rating} />
          <span className="text-[13px] font-semibold text-foreground">
            {lender.rating.toFixed(1).replace(".", ",")} av 5
          </span>
        </div>
      </div>

      <ul className="mt-4 space-y-3">
        {items.map((r, i) => (
          <li key={i}>
            <a
              href={r.href}
              target="_blank"
              rel="noopener noreferrer nofollow"
              aria-label={`Läs fler omdömen om ${lender.name} på ${r.source}`}
              className="block rounded-md border border-border bg-card p-4 !text-inherit !no-underline transition-colors hover:border-primary/50"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <StarRating rating={r.stars} />
                  <span className="text-[13px] font-bold text-foreground">{r.name}</span>
                </div>
                <span className="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  {r.source} <ExternalLink className="h-3 w-3" />
                </span>
              </div>
              <p className="mt-2 text-[14px] leading-relaxed text-foreground/80">{r.text}</p>
              <p className="mt-1.5 text-[11px] text-muted-foreground">{r.when}</p>
            </a>
          </li>
        ))}
      </ul>
      <p className="mt-2 text-[12px] text-muted-foreground">
        Omdömen är sammanställda från publika recensioner. Klicka på ett omdöme för att läsa fler.
      </p>
    </section>
  );
}
